import React, { useCallback } from 'react'
import { LocationFilter, LocationFilterHandle } from '../../search'
import { Desktop, Mobile } from '../../../components/media'
import Modal from '../../../components/modal'
import { Button } from '../../../components/button'
import { PriceFormField, Select } from '../../../components/inputs'
import Link from 'next/link'
import Router from 'next/router'
import { ROUTES } from '../../../constants'
import useLocationFilter from './hooks/use-location'
import useFilter from './hooks/use-filter'

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

const HeroSection = () => {
  const {
    location,
    distance,
    showLocationModal,
    setLocation,
    setDistance,
    setShowLocationModal,
  } = useLocationFilter()

  const {
    selectedMake,
    setSelectedMake,
    makes,
    normalizedMakes,
    isMakesLoading,
    defaultMakeOption,
    selectedModel,
    setSelectedModel,
    models,
    normalizedModels,
    isModelsLoading,
    defaultModelOption,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
  } = useFilter()

  const handleLocationChange = useCallback(
    (location) => {
      setLocation(location)
    },
    [setLocation],
  )

  const handleDistanceChange = useCallback(
    (distance) => {
      setDistance(distance)
    },
    [setDistance],
  )

  const handleMakeChange = useCallback(
    ({ value }) => {
      setSelectedMake({ value, label: normalizedMakes[value] })
      if (value === defaultMakeOption.value) {
        setSelectedModel(defaultModelOption)
      }
    },
    [defaultMakeOption, defaultModelOption, normalizedMakes, setSelectedMake, setSelectedModel],
  )

  const handleModelChange = useCallback(
    ({ value }) => {
      setSelectedModel({ value, label: normalizedModels[value] })
    },
    [normalizedModels, setSelectedModel],
  )

  const handleMinPriceChange = useCallback(
    (event) => {
      setMinPrice(event.target.value)
    },
    [setMinPrice],
  )
  const handleMaxPriceChange = useCallback(
    (event) => {
      setMaxPrice(event.target.value)
    },
    [setMaxPrice],
  )

  const handleFilterClick = useCallback(() => {
    let resultOptions = {}

    if (location.zipCode) {
      resultOptions = { ...resultOptions, zipCode: location.zipCode }
    }

    if (location.latitude && location.longitude) {
      resultOptions = {
        ...resultOptions,
        latitude: location.latitude,
        longitude: location.longitude,
      }
    }

    if (distance) {
      resultOptions = { ...resultOptions, distance }
    }

    if (minPrice) {
      resultOptions = { ...resultOptions, priceStart: minPrice }
    }

    if (maxPrice) {
      resultOptions = { ...resultOptions, priceEnd: maxPrice }
    }

    if (selectedMake?.value !== 0) {
      const make =
        selectedMake?.value === 0
          ? {}
          : { [selectedMake.value]: { id: selectedMake.value, name: selectedMake.label } }

      resultOptions = { ...resultOptions, makes: JSON.stringify(make) }
    }

    if (selectedModel?.value !== 0) {
      const model =
        selectedModel?.value === 0
          ? {}
          : {
              [selectedModel.label.toLowerCase()]: {
                id: selectedModel.value,
                name: selectedModel.label,
              },
            }

      resultOptions = { ...resultOptions, models: JSON.stringify(model) }
    }

    const params = new URLSearchParams(resultOptions)

    Router.push(`${ROUTES.Search}?${params}`)
  }, [distance, location, maxPrice, minPrice, selectedMake, selectedModel])

  return (
    <div className='root'>
      <h1 className='page-title'>Buy your next vehicle safely and securely.</h1>

      <div className='location'>
        <LocationFilterHandle
          name='location'
          zipCode={location.zipCode}
          onClick={() => setShowLocationModal(true)}
        />
        {showLocationModal && (
          <>
            <Desktop>
              <Modal
                className='filterBoundary'
                name='location'
                onClose={() => setShowLocationModal(false)}
              >
                <LocationFilter
                  location={location}
                  distance={distance}
                  onLocationChange={handleLocationChange}
                  onDistanceChange={handleDistanceChange}
                  onHeroClose={() => setShowLocationModal(false)}
                />
              </Modal>
            </Desktop>
            <Mobile>
              <Modal name='location' onClose={() => setShowLocationModal(false)}>
                <LocationFilter
                  location={location}
                  distance={distance}
                  onLocationChange={handleLocationChange}
                  onDistanceChange={handleDistanceChange}
                  onHeroClose={() => setShowLocationModal(false)}
                />
                <Button className='locationFilterBtn' onClick={() => setShowLocationModal(false)}>
                  Search
                </Button>
              </Modal>
            </Mobile>
          </>
        )}
      </div>

      <div className='filter'>
        <Select
          isLoading={isMakesLoading}
          aria-label='makes'
          id='makes'
          instanceId='makes'
          name='makes'
          placeholder='Makes'
          value={selectedMake}
          options={makes}
          onChange={handleMakeChange}
        />
        <Select
          isLoading={isModelsLoading}
          isDisabled={selectedMake.value === 0}
          aria-label='models'
          id='models'
          instanceId='models'
          name='models'
          placeholder='Models'
          value={selectedModel}
          options={models}
          onChange={handleModelChange}
        />
        <PriceFormField
          name='start'
          value={minPrice}
          placeholder='Min price'
          onChange={handleMinPriceChange}
        />
        <PriceFormField
          name='start'
          value={maxPrice}
          placeholder='Max price'
          onChange={handleMaxPriceChange}
        />
      </div>

      <div className='filter-button'>
        <Button onClick={handleFilterClick}>Find a car</Button>
        <Link legacyBehavior href={ROUTES.Search}>
          <a className='search-button'>Advanced search</a>
        </Link>
      </div>

      <style jsx>{`
        .root {
          width: 100%;
          min-height: 52.5rem;
          padding: 5.9rem;
          background-blend-mode: color;
          background-position: center;
          background-repeat: no-repeat;
          background-size: cover;
          background-image: url(/images/buy/hero-section/hero@2x.jpg);

          .page-title {
            font-size: 5.4rem;
            font-weight: 700;
            color: var(--common-white);
            text-align: center;
            max-width: 55rem;
            margin: 0 auto;
            letter-spacing: 0;
            line-height: 1.2;
          }

          .location {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: 2.4rem;
          }

          .filter {
            margin: 3.7rem auto 0;
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(32px, 1fr));
            gap: 1.4rem;
            max-width: 134rem;
          }

          .filter-button {
            margin-top: 6.6rem;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 1.4rem;

            .search-button {
              font-size: 1.8rem;
              color: var(--common-white);
            }
          }
        }

        :global(.locationFilterBtn) {
          position: fixed;
          left: 1.3rem;
          bottom: 2.1rem;
          width: calc(100vw - 2.6rem);
        }

        @media (max-width: 1024px) {
          .root {
            min-height: 24rem;
            height: unset;
            max-height: unset;
            padding: 4rem 5rem;
            background-image: url(/images/buy/hero-section/hero.jpg);

            .page-title {
              font-size: 2rem;
            }

            .filter {
              margin-top: 2rem;
              grid-template-columns: repeat(2, 1fr);
            }

            .filter-button {
              margin-top: 3rem;

              .search-button {
                font-size: 1.4rem;
              }
            }
          }
        }
        @media (max-width: 500px) {
          .root {
            .filter {
              grid-template-columns: 1fr;
            }
          }
        }
      `}</style>
    </div>
  )
}

export default HeroSection
